import { useState, useEffect, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { APIContext } from 'context/APIContext'
import { UserPlusIcon } from '@heroicons/react/24/outline'
import { ModalKey, ModalContext } from 'components/Modals/ModalContext'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { resetActiveWorkoutState } from 'redux/slices/activeWorkout'
import { resetActiveWorkoutV5State } from 'redux/slices/activeWorkoutV5'
import { HasOrganizationPaymentMethodBeenAdded } from 'functions/organizationUtils'
import { Megaphone, UsersRound, UserRound, ClipboardCheck, Dumbbell } from "lucide-react";
import { ff, flags } from 'functions/roleBasedAccessUtils'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import BasicStatCard from 'components/Home/BasicStatCard'
import ContentFrame from 'components/Frame/ContentFrame'
import ComplianceRadialDials from 'components/Reporting/ComplianceRadialDials'
import ActiveInjuriesBox from 'components/ActiveInjuries/ActiveInjuriesBox'
import RecentActivity from 'components/Home/RecentActivity'

const LATEST_ACTIVE_WORKOUT_SLICE_VERSION = '8.0.0'
const LATEST_ACTIVE_WORKOUT_V5_SLICE_VERSION = '2.0.0'

export default function Home() {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { GetOrganizationStats } = useContext(APIContext)
    const { ShowModal } = useContext(ModalContext)

    const organizationId = useSelector(state => state.user.organizationId);
    const userId = useSelector(state => state.user.id);
    const userType = useSelector((state) => state.user.type)
    const firstName = useSelector((state) => state.user.firstName)
    const activeWorkout = useSelector((state) => state.activeWorkout)
    const activeWorkoutV5 = useSelector((state) => state.activeWorkoutV5)
    const requirePaymentMethodToInviteAthletes = useSelector((state) => state.user.requirePaymentMethodToInviteAthletes)
    const organizationAdminStripeCustomerId = useSelector((state) => state.user.organizationAdminStripeCustomerId)
    const enableKinesisUserGroups = useSelector((state) => state.user.enableKinesisUserGroups)

    const canAccessKinesisUserGroups = ff(flags.canAccessKinesisUserGroups, { userType: userType, enableKinesisUserGroups: enableKinesisUserGroups })

    const [organizationStats, setOrganizationStats] = useState(null)

    const LoadOrganizationStats = async () => {

        setOrganizationStats(null)

        const startDate = moment().add(-3, 'week').startOf('isoWeek').format('YYYY-MM-DD')
        const endDate = moment().endOf('isoWeek').format('YYYY-MM-DD')

        const newOrganizationStats = await GetOrganizationStats(organizationId, userId, userType, startDate, endDate)

        setOrganizationStats(newOrganizationStats)

    }

    const OnAddAthletes = () => {
        if (HasOrganizationPaymentMethodBeenAdded(requirePaymentMethodToInviteAthletes, organizationAdminStripeCustomerId) === false) {
            ShowPaymentMethodRequiredWarning()
            return
        }

        navigate('/onboarding/athletes/options')
    }

    const ShowPaymentMethodRequiredWarning = () => {
        const modalData = {
            key: ModalKey.ActionWarning,
            size: 'quarter',
            titleText: 'Unable to add athletes',
            bodyText: [
                'In order to add additional athletes a payment method must be added to the organization.',
                'A payment method can be added by the organization owner'
            ],
            buttonText: 'Add payment method',
            buttonAction: () => navigate('/settings/billing')
        }

        ShowModal(modalData)
    }

    const ResetOutdatedActiveWorkoutSlice = () => {
        if (activeWorkout.version !== LATEST_ACTIVE_WORKOUT_SLICE_VERSION) {
            dispatch(resetActiveWorkoutState())
        }
    }

    const ResetOutdatedActiveWorkoutV5Slice = () => {
        if (activeWorkoutV5?.version !== LATEST_ACTIVE_WORKOUT_V5_SLICE_VERSION) {
            dispatch(resetActiveWorkoutV5State())
        }
    }

    useEffect(() => { LoadOrganizationStats() }, [])

    useEffect(() => { ResetOutdatedActiveWorkoutSlice() }, [activeWorkout])

    useEffect(() => { ResetOutdatedActiveWorkoutV5Slice() }, [activeWorkoutV5])
    
    if (organizationStats === null) {
        return (
            <ScreenFrame>
                <div className="h-full space-y-4">
                    <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
                        <div className='h-20 bg-gray-200 rounded-lg animate-pulse' />
                    </div>
                    <div className='h-96 bg-gray-200 rounded-lg animate-pulse' />
                </div> 
            </ScreenFrame>
        )
    }

    const coachCount = organizationStats?.coachCount || 0
    const groupCount = organizationStats?.groupCount || 0
    const athleteCount = organizationStats?.athleteCount || 0
    const assessmentCount = organizationStats?.assessmentCount || 0
    const workoutCount = organizationStats?.workoutCount || 0
    const workoutCompliance = organizationStats?.workoutCompliance || null
    const injuries = organizationStats?.injuries || null
    const recentActivity = organizationStats?.recentActivity || null

    return (
        <ScreenFrame>
            <div className='flex items-center justify-center gap-x-2'>
                <h1 className="text-2xl font-semibold text-gray-900 text-center py-1">Welcome back, {firstName}</h1>
                <div className='text-2xl'>👋</div>
            </div>

            <div className="grid grid-cols-3 gap-4 h-[441px]">
                <div className='space-y-4'>
                    <BasicStatCard
                        title='Coaches'
                        icon={<Megaphone className="h-6 w-6 text-white" />}
                        value={coachCount}
                        onClick={() => navigate('/coaches')}
                    />

                    {canAccessKinesisUserGroups && (
                        <BasicStatCard
                            title='Groups'
                            icon={<UsersRound className="h-6 w-6 text-white" />}
                            value={groupCount}
                            onClick={() => navigate('/groups')}
                        />
                    )}

                    <BasicStatCard
                        title='Athletes'
                        icon={<UserRound className="h-6 w-6 text-white" />}
                        value={athleteCount}
                        onClick={() => navigate('/athletes')}
                    />

                    {!canAccessKinesisUserGroups && (
                        <BasicStatCard
                            title='Assessments'
                            icon={<ClipboardCheck className="h-6 w-6 text-white" />}
                            value={assessmentCount}
                            onClick={() => navigate('/assessments')}
                        />
                    )}

                    <BasicStatCard
                        title='Workouts'
                        icon={<Dumbbell className="h-6 w-6 text-white" />}
                        value={workoutCount}
                        onClick={() => navigate('/workouts')}
                    />
                </div>

                <ContentFrame
                    titleText="Compliance"
                    titleIcon={<InformationCircleIcon className="mt-0.5 w-5 h-5 text-gray-800" />}
                    toolTipText="Program compliance over the past 4 weeks"
                >
                    <ComplianceRadialDials workoutCompliance={workoutCompliance} />
                </ContentFrame>

                <ActiveInjuriesBox injuries={injuries} />
            </div>

            <RecentActivity recentActivity={recentActivity} />

            <div className="fixed bottom-8 right-8 z-50">
                <button
                    className="w-full text-white p-4 rounded-2xl bg-orange-400 shadow-lg flex items-center justify-center gap-2 transition-colors"
                    onClick={OnAddAthletes}
                >
                    <UserPlusIcon className="h-6 w-6" />
                    <span className="font-semibold">Add Athletes</span>
                </button>
            </div>
        </ScreenFrame>
    )
}
